import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AutoComplete } from 'primereact/autocomplete';
import { listLinksCourses } from '../../api/links';
import { LEVELS, generateBlocker, utcToday } from './linksPuzzles';
import { puzzleShape } from './LinksReviewPane';
import './links.css';

const MAX_PINS = 4;

const courseLabel = (c) => `${c.name} · ${c.last_used ? `last used ${c.last_used}` : 'never used'}${c.rested ? '' : ' (inside the reuse window)'}`;

// Queue a draft for a date at a level. Pinned courses become the first
// groups; the generator fills the rest, never-used courses first, then
// least recently used, and warns on a board that brings a course back
// inside the 60-day reuse window. The job runs in the background; its
// state shows in the list's generation strip.
export default function LinksGeneratePane({ token, initial, puzzles, generations, busy, onGenerate, onCancel }) {
    const [date, setDate] = useState(initial.date);
    const [level, setLevel] = useState(initial.level);
    const [pins, setPins] = useState([]);
    const [courses, setCourses] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [loadError, setLoadError] = useState('');

    useEffect(() => { setDate(initial.date); setLevel(initial.level); }, [initial.date, initial.level]);

    useEffect(() => {
        listLinksCourses(token).then(setCourses).catch(err => setLoadError(err.message || 'Loading courses failed.'));
    }, [token]);

    const search = (e) => {
        const query = e.query.trim().toLowerCase();
        const pinned = new Set(pins.map(c => c.id));
        setSuggestions(courses.filter(c => !pinned.has(c.id) && c.name.toLowerCase().includes(query)).slice(0, 20));
    };

    const blocker = generateBlocker(date, puzzles, generations);
    const replaces = puzzles.find(p => p.status === 'draft' && p.date === date);

    const submit = () => onGenerate({ date, level, courseIds: pins.map(c => c.id) });

    return (
        <div className="lk-pane">
            <div className="lk-pane-head">
                <h3>Generate a puzzle</h3>
            </div>
            <p className="lk-help">
                Takes 1-3 minutes in the background and adds a draft on that date for you to review and approve.
            </p>
            <div className="lk-form">
                <label className="lk-label" htmlFor="lk-gen-date">Date (UTC)</label>
                <input
                    id="lk-gen-date"
                    type="date"
                    className="lk-input lk-input-date"
                    min={utcToday()}
                    value={date}
                    onChange={e => setDate(e.target.value)}
                />
                <span className="lk-label">Level</span>
                <div className="lk-levels" role="radiogroup" aria-label="Level">
                    {LEVELS.map(l => (
                        <label key={l.value} className="lk-level-option">
                            <input
                                type="radio"
                                name="lk-level"
                                value={l.value}
                                checked={level === l.value}
                                onChange={() => setLevel(l.value)}
                            />
                            <span className={`lk-level lk-level-${l.value}`}>{l.label}</span>
                        </label>
                    ))}
                </div>
                <label className="lk-label" htmlFor="lk-gen-courses">Pinned courses (optional, up to {MAX_PINS})</label>
                <AutoComplete
                    inputId="lk-gen-courses"
                    className="lk-pins"
                    multiple
                    value={pins}
                    suggestions={suggestions}
                    completeMethod={search}
                    field="name"
                    itemTemplate={courseLabel}
                    onChange={e => setPins((e.value || []).slice(0, MAX_PINS))}
                    placeholder={pins.length ? '' : 'Search published courses'}
                />
                {loadError && <div className="lk-error">{loadError}</div>}
            </div>
            {blocker && date && <p className="lk-muted">{blocker}</p>}
            {!blocker && replaces && (
                <p className="lk-muted">Replaces draft #{replaces.id} on {date} when it finishes.</p>
            )}
            <div className="lk-actions">
                <button type="button" className="lk-btn lk-btn-primary" disabled={busy || !!blocker} onClick={submit}>
                    Generate
                </button>
                <button type="button" className="lk-btn" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
}

export const generationShape = PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    level: PropTypes.string,
    status: PropTypes.string,
    progress: PropTypes.string,
    error: PropTypes.string,
    puzzle_id: PropTypes.number,
});

LinksGeneratePane.propTypes = {
    token: PropTypes.string.isRequired,
    initial: PropTypes.shape({ date: PropTypes.string, level: PropTypes.string }).isRequired,
    puzzles: PropTypes.arrayOf(puzzleShape).isRequired,
    generations: PropTypes.arrayOf(generationShape).isRequired,
    busy: PropTypes.bool,
    onGenerate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

LinksGeneratePane.defaultProps = { busy: false };
